/**
 * Regras do "Continuar Assistindo" (compartilhadas entre Web e Stick).
 *
 * - Filme marcado como assistido sai do "Continuar Assistindo".
 * - Série: ao terminar o último episódio da ÚLTIMA temporada, a série é marcada como assistida e sai.
 * - Se o provedor atualizar a série DEPOIS disso (temporada nova, episódio semanal), o campo
 *   `lastModified` (last_modified do provedor) fica mais novo que a última vez que a pessoa assistiu →
 *   a série VOLTA para o "Continuar Assistindo" e a marca de assistida é retirada.
 */
import type { Series } from '../types/iptv';

type HistoryItem = { contentId?: string; seriesId?: string; updatedAt?: string };

/** O item do histórico pertence a esta série? (mesma regra usada nas telas) */
export function historyMatchesSeries(h: HistoryItem | null | undefined, s: Series): boolean {
  if (!h || !h.contentId) return false;
  const ids = [s.id, `ser-${s.id}`, `ser-xtream-${s.id}`];
  if (h.seriesId && ids.includes(h.seriesId)) return true;
  if (ids.includes(h.contentId)) return true;
  return !!(s.episodes && s.episodes.some((ep) => ep.id === h.contentId));
}

/** Última vez (ms) que o perfil assistiu algo desta série (0 = nunca). */
export function lastSeriesActivity(s: Series, history: HistoryItem[] | undefined): number {
  let last = 0;
  for (const h of history || []) {
    if (!historyMatchesSeries(h, s)) continue;
    const t = new Date(h.updatedAt || 0).getTime();
    if (!isNaN(t) && t > last) last = t;
  }
  return last;
}

/** O provedor atualizou a série depois da última vez que a pessoa assistiu (temporada/episódio novo). */
export function seriesUpdatedSinceLastWatch(s: Series, history: HistoryItem[] | undefined): boolean {
  const updated = Number((s as any).lastModified || 0);
  if (!updated) return false;
  const last = lastSeriesActivity(s, history);
  return last > 0 && updated > last;
}

/** Série terminada (marcada como assistida e sem novidade desde então) → fora do "Continuar Assistindo". */
export function isSeriesFinished(
  s: Series,
  history: HistoryItem[] | undefined,
  isWatched: (id: string) => boolean
): boolean {
  return isWatched(s.id) && !seriesUpdatedSinceLastWatch(s, history);
}

/** Converte o last_modified do provedor (segundos ou ms) para ms. */
export function providerTimestampToMs(raw: unknown): number | undefined {
  const n = Number(raw);
  if (!n || isNaN(n)) return undefined;
  return n < 1e12 ? n * 1000 : n;
}
